/**
 * Quartiers SEO — extraits depuis Google Autocomplete FR-MA et nettoyés
 * (cf. apps/web/scripts/seo-data/08-build-quartiers.mjs).
 *
 * Critères :
 *  - ≥ 5 spécialités cherchées dans le quartier (signal volume)
 *  - max 12 quartiers par ville
 *  - dédupe variantes (témara/temara, fès/fes, salé/sale)
 *
 * Utilisé par la route /[specialite]/[ville]/[quartier] pour générer des
 * pages canoniques type "dentiste casablanca maarif" (intent fort, gap
 * concurrent vs Dabadoc/med.ma qui n'ont pas ce niveau de granularité).
 */

export type Quartier = {
  /** Slug URL-safe (ex. "sidi-maarouf") */
  slug: string;
  /** Nom affiché FR (ex. "Sidi Maârouf") */
  name: string;
  /** Translittération arabe (ex. "سيدي معروف") — affichée sur /ar pages.
   *  Si absent, le nom FR est utilisé en fallback. */
  nameAr?: string;
  /** Nom anglais — souvent identique à name FR (toponyme). Si absent,
   *  fallback sur name FR. */
  nameEn?: string;
  /** Slug ville sahha (ex. "casablanca") */
  citySlug: string;
  /** Nb de spécialités où le quartier ressort en autocomplete */
  specCount: number;
  /** Nb d'occurrences brutes dans autocomplete (force du signal) */
  count: number;
  /** Aliases pour le matching dans addressLine1/addressLine2/neighborhood */
  matchAliases?: string[];
};

// Translittérations arabes : noms officiels marocains usuels (panneaux, mairies,
// presse arabophone). Pour les quartiers à toponyme français (Bourgogne, Beauséjour,
// Racine, Belvédère, Iberia), la version arabe est la translittération phonétique
// (usage répandu sur Google Maps + Telecontact AR). EN : nom inchangé sauf "Hay X"
// qui se traduit "X district" en anglais (cohérent avec la convention Google Maps EN).
export const QUARTIERS: Quartier[] = [
  { slug: "wifak", name: "Wifak", nameAr: "الوفاق", nameEn: "Wifak", citySlug: "temara", specCount: 11, count: 26 },
  { slug: "massira-1", name: "Massira 1", nameAr: "المسيرة 1", nameEn: "Massira 1", citySlug: "temara", specCount: 12, count: 24, matchAliases: ["massira", "el massira"] },
  { slug: "oulad-mtaa", name: "Oulad Mtaa", nameAr: "أولاد مطاع", nameEn: "Oulad Mtaa", citySlug: "temara", specCount: 8, count: 18 },
  { slug: "atlas", name: "Atlas", nameAr: "أطلس", nameEn: "Atlas", citySlug: "fes", specCount: 7, count: 16 },
  { slug: "hamria", name: "Hamria", nameAr: "الحمرية", nameEn: "Hamria", citySlug: "meknes", specCount: 12, count: 14, matchAliases: ["hamriya"] },
  { slug: "tabriquet", name: "Tabriquet", nameAr: "تابريكت", nameEn: "Tabriquet", citySlug: "sale", specCount: 11, count: 13 },
  { slug: "hay-karima", name: "Hay Karima", nameAr: "حي كريمة", nameEn: "Karima district", citySlug: "sale", specCount: 11, count: 13, matchAliases: ["karima"] },
  { slug: "oulfa", name: "Oulfa", nameAr: "الألفة", nameEn: "Oulfa", citySlug: "casablanca", specCount: 12, count: 12, matchAliases: ["hay oulfa"] },
  { slug: "agdal", name: "Agdal", nameAr: "أكدال", nameEn: "Agdal", citySlug: "rabat", specCount: 12, count: 12, matchAliases: ["agdal-riad"] },
  { slug: "hassan", name: "Hassan", nameAr: "حسان", nameEn: "Hassan", citySlug: "rabat", specCount: 12, count: 12, matchAliases: ["quartier hassan"] },
  { slug: "hay-riad", name: "Hay Riad", nameAr: "حي الرياض", nameEn: "Hay Riad", citySlug: "rabat", specCount: 12, count: 12, matchAliases: ["riad"] },
  { slug: "massira", name: "Massira", nameAr: "المسيرة", nameEn: "Massira", citySlug: "marrakech", specCount: 12, count: 12, matchAliases: ["el massira", "massira 1", "massira 2"] },
  { slug: "talborjt", name: "Talborjt", nameAr: "تالبرجت", nameEn: "Talborjt", citySlug: "agadir", specCount: 12, count: 12 },
  { slug: "massira-2", name: "Massira 2", nameAr: "المسيرة 2", nameEn: "Massira 2", citySlug: "temara", specCount: 6, count: 12 },
  { slug: "maarif", name: "Maârif", nameAr: "المعاريف", nameEn: "Maârif", citySlug: "casablanca", specCount: 11, count: 11, matchAliases: ["maârif", "marif", "ma'arif"] },
  { slug: "gueliz", name: "Guéliz", nameAr: "جيليز", nameEn: "Guéliz", citySlug: "marrakech", specCount: 11, count: 11, matchAliases: ["guéliz"] },
  { slug: "mhamid", name: "Mhamid", nameAr: "المحاميد", nameEn: "Mhamid", citySlug: "marrakech", specCount: 11, count: 11, matchAliases: ["m'hamid"] },
  { slug: "bettana", name: "Bettana", nameAr: "بطانة", nameEn: "Bettana", citySlug: "sale", specCount: 10, count: 11 },
  { slug: "wilaya", name: "Wilaya", nameAr: "الولاية", nameEn: "Wilaya", citySlug: "tetouan", specCount: 5, count: 10 },
  { slug: "abdelmoumen", name: "Abdelmoumen", nameAr: "عبد المؤمن", nameEn: "Abdelmoumen", citySlug: "casablanca", specCount: 9, count: 9, matchAliases: ["abdel moumen", "abdelmoumen"] },
  { slug: "daoudiate", name: "Daoudiate", nameAr: "الدوديات", nameEn: "Daoudiate", citySlug: "marrakech", specCount: 9, count: 9 },
  { slug: "dakhla", name: "Dakhla", nameAr: "الداخلة", nameEn: "Dakhla", citySlug: "agadir", specCount: 9, count: 9 },
  { slug: "sidi-maarouf", name: "Sidi Maârouf", nameAr: "سيدي معروف", nameEn: "Sidi Maârouf", citySlug: "casablanca", specCount: 8, count: 8, matchAliases: ["sidi maârouf", "sidi marouf", "sidi maarouf"] },
  { slug: "bourgogne", name: "Bourgogne", nameAr: "بورغوني", nameEn: "Bourgogne", citySlug: "casablanca", specCount: 8, count: 8 },
  { slug: "cym", name: "CYM", nameAr: "حي يعقوب المنصور", nameEn: "CYM (Yacoub El Mansour)", citySlug: "rabat", specCount: 8, count: 8, matchAliases: ["cym", "cite yacoub el mansour"] },
  { slug: "marjane", name: "Marjane", nameAr: "مرجان", nameEn: "Marjane", citySlug: "meknes", specCount: 8, count: 8 },
  { slug: "hay-chmaou", name: "Hay Chmaou", nameAr: "حي الشماعو", nameEn: "Hay Chmaou", citySlug: "sale", specCount: 8, count: 8, matchAliases: ["chmaou"] },
  { slug: "hay-salam", name: "Hay Salam", nameAr: "حي السلام", nameEn: "Hay Salam", citySlug: "sale", specCount: 8, count: 8, matchAliases: ["salam"] },
  { slug: "karia", name: "Karia", nameAr: "القرية", nameEn: "Karia", citySlug: "sale", specCount: 7, count: 7 },
  { slug: "laayayda", name: "Laâyayda", nameAr: "العيايدة", nameEn: "Laayayda", citySlug: "sale", specCount: 3, count: 6 },
  { slug: "bab-doukkala", name: "Bab Doukkala", nameAr: "باب دكالة", nameEn: "Bab Doukkala", citySlug: "marrakech", specCount: 7, count: 7 },
  { slug: "iberia", name: "Iberia", nameAr: "إيبيريا", nameEn: "Iberia", citySlug: "tanger", specCount: 6, count: 6 },
  { slug: "hay-mohammadi", name: "Hay Mohammadi", nameAr: "الحي المحمدي", nameEn: "Hay Mohammadi", citySlug: "casablanca", specCount: 6, count: 6, matchAliases: ["mohammadi"] },
  { slug: "yacoub-el-mansour", name: "Yacoub El Mansour", nameAr: "يعقوب المنصور", nameEn: "Yacoub El Mansour", citySlug: "rabat", specCount: 6, count: 6, matchAliases: ["yacoub", "el mansour"] },
  // Quartiers Casablanca ajoutés 2026-05-08 — détectés via DB (ces slugs
  // étaient stockés comme `cityId` de physicians scrapés mais inconnus de
  // QUARTIERS, ce qui produisait des 404 sur /[spe]/casablanca/[quartier].
  // Source : nginx logs + DB cities table avec count physicians.
  { slug: "anfa", name: "Anfa", nameAr: "أنفا", nameEn: "Anfa", citySlug: "casablanca", specCount: 7, count: 7 },
  { slug: "ain-chock", name: "Aïn Chock", nameAr: "عين الشق", nameEn: "Aïn Chock", citySlug: "casablanca", specCount: 14, count: 14, matchAliases: ["ain chock", "aïn chock"] },
  { slug: "hay-hassani", name: "Hay Hassani", nameAr: "الحي الحسني", nameEn: "Hay Hassani", citySlug: "casablanca", specCount: 12, count: 12, matchAliases: ["hassani"] },
  { slug: "ain-sebaa", name: "Aïn Sebaâ", nameAr: "عين السبع", nameEn: "Aïn Sebaâ", citySlug: "casablanca", specCount: 8, count: 8, matchAliases: ["ain sebaa", "aïn sebaâ"] },
  { slug: "sidi-bernoussi", name: "Sidi Bernoussi", nameAr: "سيدي البرنوصي", nameEn: "Sidi Bernoussi", citySlug: "casablanca", specCount: 5, count: 5, matchAliases: ["bernoussi"] },
  { slug: "oasis", name: "Oasis", nameAr: "الواحة", nameEn: "Oasis", citySlug: "casablanca", specCount: 11, count: 11 },
  { slug: "beausejour", name: "Beauséjour", nameAr: "بوسيجور", nameEn: "Beauséjour", citySlug: "casablanca", specCount: 5, count: 5, matchAliases: ["beauséjour"] },
  { slug: "racine", name: "Racine", nameAr: "راسين", nameEn: "Racine", citySlug: "casablanca", specCount: 3, count: 3 },
  { slug: "belvedere", name: "Belvédère", nameAr: "بلفيدير", nameEn: "Belvédère", citySlug: "casablanca", specCount: 2, count: 2, matchAliases: ["belvédère"] },
  // ── Ajouts 2026-09-21 (chantier « médecins × ville ») ──────────────────────
  // Source DIFFÉRENTE des entrées ci-dessus : ces 4 quartiers ne viennent pas
  // de l'autocomplete Google mais du GATE DE PRODUCTION des pages quartier,
  // rejoué à l'identique (MIN_RESULTS = 5, `p.specialties.includes(spé
  // featured)` + `physicianIsInCity` + `matchesQuartier`, cf.
  // lib/seo/quartier-page-render.tsx et le générateur de
  // specialites-quartiers.xml). Contrôle de non-régression avant ajout : le
  // rejeu sur les 43 entrées ci-dessus rend exactement 219 combos = 657 URLs,
  // soit le sitemap servi — donc le prédicat est bien celui de la production.
  //   specCount = nombre de spécialités featured qui passent le seuil de 5
  //   count     = nombre de fiches de la ville que `matchesQuartier` retient
  // Mesures du 2026-09-21 sur le catalogue (44 605 fiches), prises APRÈS
  // déclaration du quartier : déclarer un slug l'ajoute à RAW_SLUGS_BY_CITY
  // (url-builder.ts:270), donc les fiches dont le `citySlug` brut EST ce slug
  // entrent dans le roster. C'est ce qui fait 30 et non 29 pour `errahma`.
  // Apport : 24 combos → 72 URLs (FR/AR/EN), sans contenu nouveau à écrire ;
  // specialites-quartiers.xml passe de 219 combos / 657 URLs à 243 / 729
  // (rejeu du gate : 219/657 sans ces 4 entrées = exactement le sitemap servi).
  //
  // CE QUE ÇA CHANGE AU RENDU (contrairement à ce qui avait été annoncé) :
  // QUARTIERS alimente aussi le bloc « <Spé> par quartier à <ville> » et
  // l'énumération de l'intro éditoriale du listing
  // ([locale]/[specialite]/[ville]/page.tsx:329 puis :504 et :569). Les
  // listings de Casablanca, Marrakech et Tanger gagnent donc des liens, sur
  // les 3 locales. Ces liens restent propres : depuis le 2026-09-21 le bloc
  // est filtré par `hasIndexableQuartier()` (lib/seo/specialty-meta.ts:267 —
  // MÊME seuil de 5 que la page cible), donc seuls les 24 combos qui passent
  // le gate sont liés, jamais /cardiologue/tanger/castilla & consorts.
  //
  // EFFET DE BORD À SURVEILLER — `errahma` : déclarer ce quartier fait passer
  // canonicalCity("errahma") de null à "casablanca" (url-builder.ts:296, via
  // cityFromQuartierSlug). Une fiche du catalogue a `citySlug: "errahma"` —
  // dr-jihane-lahjouji-generaliste — et change donc d'URL canonique :
  // physicianUrl() renvoyait /medecin/dr-jihane-lahjouji-generaliste (fiche
  // exclue du sitemap par la garde « canonical /medecin/ »), il renvoie
  // désormais /generaliste/casablanca/jihane-lahjouji, qui entre dans
  // medecins-*.xml. Cette URL n'est PAS dans physician-slug-redirects.json ;
  // le seul redirect existant (`jihane-lahjouji-errahma`) pointe encore vers
  // /medecin/… donc la chaîne fait un saut de plus. À passer au smoke test
  // après déploiement (attendu : 200 + index,follow sur la nouvelle URL,
  // 301 sans boucle depuis l'ancienne).
  //
  // Deux candidats de la liste initiale ont été ÉCARTÉS après mesure :
  //  - `al-oulfa` (casablanca) : ses 28 fiches sont un sous-ensemble strict de
  //    celles du quartier `oulfa` déjà présent ligne 49 (recouvrement 100 %) →
  //    page en double, cannibalisation de notre propre hub ;
  //  - `al-fida` (casablanca) : deux fiches de centre ont pour URL canonique
  //    /kinesitherapeute/casablanca/al-fida (centre-de-kinesitherapie-al-fida-*).
  //    Or le dispatcher /[specialite]/[ville]/[name] rend le hub quartier AVANT
  //    de chercher un médecin ([name]/page.tsx:678) : déclarer ce quartier
  //    rendrait ces fiches injoignables à leur URL canonique, pour un seul
  //    combo (generaliste). Même classe de piège que « dakhla » ville/quartier.
  { slug: "2-mars", name: "2 Mars", nameAr: "2 مارس", nameEn: "2 Mars", citySlug: "casablanca", specCount: 12, count: 206, matchAliases: ["2 mars", "deux mars"] },
  { slug: "allal-el-fassi", name: "Allal El Fassi", nameAr: "علال الفاسي", nameEn: "Allal El Fassi", citySlug: "marrakech", specCount: 10, count: 142 },
  { slug: "errahma", name: "Errahma", nameAr: "الرحمة", nameEn: "Errahma", citySlug: "casablanca", specCount: 1, count: 30 },
  { slug: "castilla", name: "Castilla", nameAr: "كاستيا", nameEn: "Castilla", citySlug: "tanger", specCount: 1, count: 26 },
];

/**
 * Helper localized — retourne le nom du quartier dans la locale demandée,
 * avec fallback FR si la traduction est absente. À utiliser sur tous les
 * usages d'AFFICHAGE (HTML + meta + JSON-LD), JAMAIS sur le matching technique
 * (matchQuartier garde le nom FR car les addressLine scrapées sont en FR).
 */
export function localizedQuartierName(q: Quartier, locale: string): string {
  if (locale === "ar") return q.nameAr ?? q.name;
  if (locale === "en") return q.nameEn ?? q.name;
  return q.name;
}

const QUARTIERS_BY_KEY = new Map(QUARTIERS.map((q) => [`${q.citySlug}|${q.slug}`, q]));
const QUARTIERS_BY_CITY = new Map<string, Quartier[]>();
for (const q of QUARTIERS) {
  const arr = QUARTIERS_BY_CITY.get(q.citySlug) ?? [];
  arr.push(q);
  QUARTIERS_BY_CITY.set(q.citySlug, arr);
}

export function getQuartier(citySlug: string, quartierSlug: string): Quartier | undefined {
  return QUARTIERS_BY_KEY.get(`${citySlug}|${quartierSlug}`);
}

export function getQuartiersOfCity(citySlug: string): Quartier[] {
  return QUARTIERS_BY_CITY.get(citySlug) ?? [];
}

/**
 * Si `slug` correspond à un quartier connu (agdal, maarif, gueliz, …),
 * retourne son `citySlug` parent (rabat, casablanca, marrakech). Utilisé
 * pour récupérer la bonne ville canonique des médecins dont le `cityId`
 * pointe à tort vers un quartier — fréquent dans les imports scrapés où
 * le scraper a confondu adresse "Agdal, Rabat" avec une ville à part.
 */
const QUARTIERS_BY_SLUG = new Map<string, Quartier>();
for (const q of QUARTIERS) {
  QUARTIERS_BY_SLUG.set(q.slug, q);
}
export function cityFromQuartierSlug(slug: string): string | null {
  return QUARTIERS_BY_SLUG.get(slug)?.citySlug ?? null;
}

/** Normalise une chaîne pour le matching (NFD + lowercase + alphanum). */
function norm(s: string): string {
  return s.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Test si un médecin appartient au quartier — match sur neighborhood,
 * addressLine1, addressLine2 et googleFormattedAddress (si dispo).
 * Match permissif : compare les variantes normalisées, sans accent.
 */
export function matchesQuartier(
  doctor: { neighborhood?: string; addressLine1?: string; addressLine2?: string; googleFormattedAddress?: string },
  quartier: Quartier,
): boolean {
  const haystack = norm([doctor.neighborhood, doctor.addressLine1, doctor.addressLine2, doctor.googleFormattedAddress].filter(Boolean).join(" "));
  if (!haystack) return false;
  const needles = [quartier.name, quartier.slug.replace(/-/g, " "), ...(quartier.matchAliases ?? [])];
  for (const n of needles) {
    const normalized = norm(n);
    if (!normalized || normalized.length < 3) continue;
    // Match en mot complet (pas un substring) pour éviter les faux positifs
    const re = new RegExp(`(^|\\s)${normalized.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\s|$)`);
    if (re.test(haystack)) return true;
  }
  return false;
}
